/**
 * Shared widget chrome: title bar, states, and failure isolation.
 *
 * Two independent things can go wrong, and both are contained here so one
 * broken tile never takes the dashboard down:
 *   - a failed REQUEST  → handled by the status props (error + Retry)
 *   - a failed RENDER   → caught by WidgetErrorBoundary
 */

import React from 'react';
import Tooltip from '../../components/glass/Tooltip';
import { AlertTriangle, RefreshCw, Settings2, X } from 'lucide-react';
import type { WidgetStatus } from './useWidgetData';

interface Props {
  title: string;
  status: WidgetStatus;
  error?: string | null;
  onRetry?: () => void;
  /** Small print under the content — source name, truncation notice, etc. */
  footnote?: string;
  /** Shown instead of content when the widget is not fully configured. */
  incomplete?: string[];
  /** Hides the header entirely (text widgets supply their own heading). */
  hideHeader?: boolean;
  /**
   * Human-readable description of the action filters currently narrowing this
   * widget, e.g. "By Class: 9". Shown as a dismissible chip so a surprising
   * number is always explained by something visible on screen.
   */
  actionLabels?: string[];
  onClearActions?: () => void;
  accentColor?: string;
  /** Per-widget border colour (from `widget.config.outlineColor`) — unset
   *  keeps the class's own subtle glass hairline. Set, it also thickens the
   *  border a touch so a deliberate colour reads as one, not as a stray
   *  pixel. */
  outlineColor?: string;
  children: React.ReactNode;
}

/** Esri Operations Dashboard's own tiles are boxy — a small radius, not the
 *  rest of the app's soft glass-card rounding — so a dashboard reads as a
 *  grid of instrument panels rather than floating cards. Scoped to this one
 *  component: the shared `.gl-card` class (popups, panels, symbology…) is
 *  untouched. */
const DASHBOARD_CARD_RADIUS = 8;

export default function WidgetFrame({
  title,
  status,
  error,
  onRetry,
  footnote,
  incomplete,
  hideHeader,
  actionLabels,
  onClearActions,
  accentColor = '#2563EB',
  outlineColor,
  children,
}: Props) {
  const hasProblems = Boolean(incomplete?.length);

  return (
    // Glass KPI/chart card. `--strong` keeps chart labels and table text crisp;
    // the brief is explicit that data readability outranks the glass effect.
    // Radius and border overridden inline (see DASHBOARD_CARD_RADIUS) rather
    // than through a CSS class: `.gl-card`'s own rounding stays the app-wide
    // default everywhere else it is used. NOT `gl-card--interactive`: its
    // hover-lift (translateY + shadow) is built for a big clickable app-gallery
    // card — on a grid of dense, closely-packed tiles the cursor drifts across
    // while reading, it reads as the whole dashboard jittering underneath you.
    <div
      className="gl-card gl-surface--strong flex h-full min-h-0 flex-col overflow-hidden"
      style={{
        borderRadius: DASHBOARD_CARD_RADIUS,
        ...(outlineColor ? { border: `1.5px solid ${outlineColor}` } : {}),
      }}
    >
      {!hideHeader && (
        <div className="gl-header flex flex-shrink-0 items-center justify-between gap-2 px-3 py-2">
          <h3 className="truncate text-xs font-semibold uppercase tracking-wide text-slate-500">
            {title}
          </h3>
          {status !== 'loading' && onRetry && (
            <Tooltip content="Re-run this widget's query now">
              <button
                type="button"
                onClick={onRetry}
                aria-label={`Refresh ${title}`}
                className="rounded p-1 text-slate-300 transition hover:bg-slate-100 hover:text-slate-600"
              >
                <RefreshCw className="h-3.5 w-3.5" />
              </button>
            </Tooltip>
          )}
        </div>
      )}

      {actionLabels && actionLabels.length > 0 && (
        <div className="flex flex-shrink-0 items-center gap-1.5 px-3 pb-1.5 pt-0.5">
          <span
            className="min-w-0 flex-1 truncate rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-700"
            title={actionLabels.join(' · ')}
          >
            {actionLabels.join(' · ')}
          </span>
          {onClearActions && (
            <Tooltip content="Clear the filter another widget is applying here">
              <button
                type="button"
                onClick={onClearActions}
                aria-label="Clear the filter applied by another widget"
                className="flex-shrink-0 rounded p-0.5 text-blue-600 transition hover:bg-blue-100"
              >
                <X className="h-3 w-3" />
              </button>
            </Tooltip>
          )}
        </div>
      )}

      <div className="relative min-h-0 flex-1 overflow-hidden">
        {hasProblems ? (
          <div className="flex h-full flex-col items-center justify-center gap-1.5 px-4 text-center">
            <Settings2 className="h-5 w-5 text-slate-300" />
            <p className="text-xs font-medium text-slate-500">Not configured yet</p>
            <ul className="space-y-0.5">
              {incomplete?.map((problem) => (
                <li key={problem} className="text-[11px] text-slate-400">
                  {problem}
                </li>
              ))}
            </ul>
          </div>
        ) : status === 'loading' ? (
          <div className="flex h-full items-center justify-center">
            <div
              className="h-5 w-5 animate-spin rounded-full border-2 border-slate-200"
              style={{ borderTopColor: accentColor }}
              role="status"
              aria-label="Loading"
            />
          </div>
        ) : status === 'error' ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 px-4 text-center">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            <p className="text-xs font-medium text-slate-600">Unable to load data</p>
            {error && <p className="line-clamp-2 text-[11px] text-slate-400">{error}</p>}
            {onRetry && (
              <button
                type="button"
                onClick={onRetry}
                className="mt-0.5 rounded-lg border border-slate-200 px-2.5 py-1 text-[11px] font-semibold text-slate-600 transition hover:bg-slate-50"
              >
                Retry
              </button>
            )}
          </div>
        ) : (
          <div className="h-full min-h-0 overflow-hidden">{children}</div>
        )}
      </div>

      {footnote && !hasProblems && status === 'ready' && (
        <p className="gl-footer flex-shrink-0 truncate px-3 py-1 text-[10px] text-slate-400">
          {footnote}
        </p>
      )}
    </div>
  );
}

/**
 * Catches render-time crashes inside one widget.
 *
 * Without this, a malformed configuration that throws during render would
 * unmount the whole dashboard tree — exactly the failure mode the brief calls
 * out. Class component because React has no hook equivalent.
 */
export class WidgetErrorBoundary extends React.Component<
  { title: string; children: React.ReactNode },
  { failed: boolean }
> {
  constructor(props: { title: string; children: React.ReactNode }) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: unknown) {
    console.error(`[dashboard] widget "${this.props.title}" crashed while rendering`, error);
  }

  render() {
    if (this.state.failed) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 text-center">
          <AlertTriangle className="h-5 w-5 text-amber-500" />
          <p className="text-xs font-semibold text-amber-800">{this.props.title}</p>
          <p className="text-[11px] text-amber-700">This widget could not be displayed.</p>
          <button
            type="button"
            onClick={() => this.setState({ failed: false })}
            className="rounded-lg border border-amber-300 bg-white px-2.5 py-1 text-[11px] font-semibold text-amber-800 transition hover:bg-amber-100"
          >
            Try again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
